import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useForm } from '@tanstack/react-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Trans, useLingui } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { ArrowLeft, CircleUser } from 'lucide-react';
import { toast } from 'sonner';
import { z } from 'zod';
import { api } from '@/lib/api.ts';
import { currentUserQueryOptions, useCurrentUser } from '@/hooks/queries/auth.ts';
import { userQueryOptions } from '@/hooks/queries/users.ts';
import type { APIUser } from '@/types/index.ts';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip.tsx';
import { PageContainer } from '@/components/page-container.tsx';
import { PasswordInput } from '@/components/password-input';
import { NrgkickSection } from '@/components/user-edit/nrgkick-section.tsx';
import { RfidSection } from '@/components/user-edit/rfid-section.tsx';

export const Route = createFileRoute('/_logged/account/edit')({
  component: AccountEditPage,
  staticData: {
    label: msg({
      comment:
        "Route label of the signed-in admin's own account edit page (/account/edit) — breadcrumb and document title. A noun phrase naming the page, not an imperative button.",
      message: 'Edit account',
    }),
  },
});

/**
 * `MyselfDetail` — the signed-in admin's own edit page (decisions 8/20-A/21):
 * email and password (an admin can't expire themselves, so no expiration
 * controls) plus the same NRGKick / RFID sections as `/users/$id/edit`, gated
 * by the admin's own feature flags. The read-only self view is the shared hub
 * (`/users/$selfId`); the bare `/account` URL stays reserved for a possible
 * future self-dashboard.
 */
function AccountEditPage() {
  const { t } = useLingui();
  const currentUser = useCurrentUser();
  const nrgkickEnabled = currentUser.features.parkadmin?.feat_nrgkick ?? false;
  const rfidEnabled = currentUser.features.parkadmin?.feat_rfid ?? false;

  return (
    <PageContainer>
      <header className='flex items-center gap-4'>
        <Tooltip>
          <TooltipTrigger
            render={
              <Button
                variant='outline'
                size='icon'
                nativeButton={false}
                aria-label={t`Back to your dashboard`}
                render={<Link to='/users/$id' params={{ id: String(currentUser.id) }} />}
              >
                <ArrowLeft />
              </Button>
            }
          />
          <TooltipContent>
            <Trans comment="Tooltip of the back button on the account edit page; leads to the admin's own user dashboard.">
              Back to your dashboard
            </Trans>
          </TooltipContent>
        </Tooltip>
        <div className='space-y-1'>
          <p className='text-muted-foreground text-xs font-medium tracking-widest uppercase'>
            <Trans comment="Small eyebrow heading above the username on the signed-in admin's own account edit page.">
              My account
            </Trans>
          </p>
          <h1 className='text-3xl font-semibold tracking-tight'>{currentUser.username}</h1>
        </div>
      </header>

      <div className='grid gap-6 md:grid-cols-2'>
        {/* key: the card's form state is seeded from the identity at mount only — without a remount, an identity swap (staff sudo) would keep the previous identity's draft. */}
        <MyAccountCard key={currentUser.id} user={currentUser} />
        {rfidEnabled ?
          // The near-empty relative cell keeps the RFID card out of the
          // row-height computation on md+, clamping it to the Account card's
          // height so a long card list scrolls inside instead of stretching
          // the row. The min-h is the floor the RFID card needs for its header,
          // add form and a few list rows, should the Account card ever shrink
          // below it.
          <div className='relative md:min-h-80'>
            <RfidSection userId={currentUser.id} className='md:absolute md:inset-0' />
          </div>
        : null}
        {nrgkickEnabled ?
          <NrgkickSection userId={currentUser.id} />
        : null}
      </div>
    </PageContainer>
  );
}

type AccountEditBody = {
  email?: string;
  new_password?: string;
};

/**
 * Own account fields, prefilled (decision 21): email shows its current value;
 * the password stays blank behind a "Change password?" switch. `PATCH
 * parkadmin/current/users/{id}/` sends only what differs — an emptied email is
 * treated as "unchanged", so clearing an email stays impossible. Invalidates
 * the `myself` query (identity line, feature flags) plus the users list and
 * the self detail entry, so every surface showing the email refreshes.
 */
function MyAccountCard({ user }: { user: APIUser }) {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const [changePassword, setChangePassword] = useState(false);
  const [defaultEmail, setDefaultEmail] = useState(user.email);

  const accountEditSchema = z.object({
    email: z.union([z.literal(''), z.email(t`Enter a valid email address`)]),
    newPassword: z.union([
      z.literal(''),
      z
        .string()
        .min(4, t`Must be at least 4 characters`)
        .max(128, t`Password is too long`),
    ]),
  });

  function changedFields(value: { email: string; newPassword: string }): AccountEditBody {
    const body: AccountEditBody = {};
    if (value.email !== '' && value.email !== defaultEmail) body.email = value.email;
    if (changePassword && value.newPassword !== '') body.new_password = value.newPassword;
    return body;
  }

  const mutation = useMutation({
    mutationFn: (body: AccountEditBody) => api.patch(`parkadmin/current/users/${user.id}/`, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: currentUserQueryOptions().queryKey });
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      void queryClient.invalidateQueries({ queryKey: userQueryOptions(user.id).queryKey });
    },
  });

  const form = useForm({
    defaultValues: { email: defaultEmail, newPassword: '' },
    validators: { onSubmit: accountEditSchema },
    onSubmit: async ({ value }) => {
      const body = changedFields(value);
      try {
        await mutation.mutateAsync(body);
        toast.success(t`Account updated.`);
        const nextEmail = body.email ?? defaultEmail;
        setDefaultEmail(nextEmail);
        setChangePassword(false);
        form.reset({ email: nextEmail, newPassword: '' });
      } catch {
        toast.error(t`Could not update your account. Please try again.`);
      }
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className='flex items-center gap-2'>
          <CircleUser className='size-5' />
          <Trans comment='Title of the card on the account edit page holding the account fields (email, password).'>
            Account
          </Trans>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <FieldGroup>
            <form.Field name='email'>
              {(field) => (
                <Field data-invalid={field.state.meta.errors.length > 0}>
                  <FieldLabel htmlFor={field.name}>
                    <Trans>Email</Trans>
                  </FieldLabel>
                  <Input
                    id={field.name}
                    name={field.name}
                    type='email'
                    autoComplete='email'
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(event) => field.handleChange(event.target.value)}
                    aria-invalid={field.state.meta.errors.length > 0}
                  />
                  <FieldError errors={field.state.meta.errors} />
                </Field>
              )}
            </form.Field>

            <div className='flex items-center gap-2'>
              <Switch
                id='change-password'
                checked={changePassword}
                onCheckedChange={(checked) => {
                  setChangePassword(checked);
                  // Clear any typed password when opting out, so it can never be
                  // submitted or trip validation — guards against an accidental reset.
                  if (!checked) form.setFieldValue('newPassword', '');
                }}
              />
              <Label htmlFor='change-password'>
                <Trans comment='Label for a toggle switch on the account edit page. When off, the New password field below is disabled and cleared, preventing an accidental password reset. Phrased as a yes/no question (Change password?).'>
                  Change password?
                </Trans>
              </Label>
            </div>

            <form.Field name='newPassword'>
              {(field) => (
                <Field data-invalid={field.state.meta.errors.length > 0}>
                  <FieldLabel htmlFor={field.name}>
                    <Trans>New password</Trans>
                  </FieldLabel>
                  <PasswordInput
                    id={field.name}
                    name={field.name}
                    autoComplete='new-password'
                    disabled={!changePassword}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(event) => field.handleChange(event.target.value)}
                    aria-invalid={field.state.meta.errors.length > 0}
                  />
                  <FieldError errors={field.state.meta.errors} />
                </Field>
              )}
            </form.Field>
          </FieldGroup>

          <div className='mt-6 flex justify-end'>
            <form.Subscribe
              selector={(state) =>
                [state.values.email, state.values.newPassword, state.isSubmitting] as const
              }
            >
              {([email, newPassword, isSubmitting]) => {
                const hasChanges = Object.keys(changedFields({ email, newPassword })).length > 0;
                return (
                  <Button type='submit' disabled={!hasChanges || isSubmitting}>
                    {isSubmitting ? t`Saving…` : t`Save`}
                  </Button>
                );
              }}
            </form.Subscribe>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
